import React, { useEffect, useState, useCallback } from "react";
import { Layout } from "@/components/Layout";
import { PageHero } from "@/components/PageHero";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Link } from "react-router-dom";
import { Plus, Trash2, TrendingUp, TrendingDown, Sparkles, Loader2, Wallet, DollarSign } from "lucide-react";

const CAT = ["subscription", "api", "one-off"];

export default function Accountant() {
  const { user } = useAuth();
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ tool: "", amount: "", currency: "USD", category: "subscription", kind: "expense", note: "" });

  const refresh = useCallback(async () => {
    const e = await api.get("/accountant/entries");
    setEntries(e.data);
    const s = await api.get("/accountant/summary");
    setSummary(s.data);
  }, []);

  useEffect(() => { if (user) refresh(); }, [user, refresh]);

  const add = async (e) => {
    e.preventDefault();
    if (!form.tool || !form.amount) return;
    await api.post("/accountant/entry", { ...form, amount: parseFloat(form.amount) });
    setForm({ ...form, tool: "", amount: "", note: "" });
    refresh();
  };

  const remove = async (id) => { await api.delete(`/accountant/entry/${id}`); refresh(); };

  const analyze = async () => {
    setLoading(true); setAnalysis(null);
    try {
      const r = await api.post("/accountant/analyze");
      setAnalysis(r.data.analysis);
    } finally { setLoading(false); }
  };

  if (!user) return <Layout hideRightPanel>
    <div className="text-center py-20">
      <Wallet size={40} className="mx-auto mb-4 text-[#F97316]"/>
      <h1 className="font-display text-4xl font-bold mb-2">Your AI accounting hub</h1>
      <p className="text-[#5B6478] mb-6">Sign in to track subscriptions, API costs, and ROI across every AI tool.</p>
      <Link to="/login" className="btn-coral inline-flex">Sign in</Link>
    </div>
  </Layout>;

  return (
    <Layout hideRightPanel>
      <PageHero
        testId="accountant-hero"
        eyebrow="AI Accountant"
        title={<>Track every dollar of <span className="italic">AI spend.</span></>}
        sub="Your subscriptions, API bills, and AI-generated income — in one place. GPT-5.4 acts as your personal accountant."
      />

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-testid="accountant-summary">
          <div className="curio-card p-5"><div className="text-[10px] uppercase tracking-widest text-[#5B6478] font-bold mb-1">Total spend</div><div className="font-display text-3xl font-bold">${summary.total_expense.toFixed(2)}</div><TrendingDown size={14} className="text-rose-500 mt-1"/></div>
          <div className="curio-card p-5"><div className="text-[10px] uppercase tracking-widest text-[#5B6478] font-bold mb-1">AI income</div><div className="font-display text-3xl font-bold">${summary.total_income.toFixed(2)}</div><TrendingUp size={14} className="text-emerald-500 mt-1"/></div>
          <div className="curio-card p-5"><div className="text-[10px] uppercase tracking-widest text-[#5B6478] font-bold mb-1">Net</div><div className={`font-display text-3xl font-bold ${summary.net >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>${summary.net.toFixed(2)}</div></div>
          <div className="curio-card p-5"><div className="text-[10px] uppercase tracking-widest text-[#5B6478] font-bold mb-1">ROI</div><div className="font-display text-3xl font-bold">{summary.roi === null ? "—" : `${(summary.roi * 100).toFixed(0)}%`}</div></div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-6">
        <div className="lg:col-span-2 curio-card p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold">Ledger</h2>
            <button onClick={analyze} disabled={loading || entries.length === 0} className="btn-coral text-xs disabled:opacity-50" data-testid="analyze-btn">
              {loading ? <><Loader2 size={12} className="animate-spin"/> Analyzing…</> : <><Sparkles size={12}/> AI analyze</>}
            </button>
          </div>
          {entries.length === 0 ? (
            <p className="text-sm text-[#5B6478]">No entries yet. Add your first subscription or income below.</p>
          ) : (
            <div className="max-h-96 overflow-y-auto">
              <table className="w-full text-sm" data-testid="entries-table">
                <thead className="text-[10px] uppercase tracking-widest text-[#5B6478]">
                  <tr className="border-b border-[#E3E8F0]"><th className="text-left py-2">Tool</th><th>Cat</th><th className="text-right">Amount</th><th></th></tr>
                </thead>
                <tbody>
                {entries.map(e => (
                  <tr key={e.id} className="border-b border-[#F1F3F8]">
                    <td className="py-2"><div>{e.tool}</div><div className="text-xs text-[#5B6478]">{e.date} · {e.note || ''}</div></td>
                    <td className="text-xs text-[#5B6478]">{e.category}</td>
                    <td className={`text-right font-semibold ${e.kind === 'income' ? 'text-emerald-600' : 'text-rose-600'}`}>{e.kind === 'income' ? '+' : '−'}${e.amount.toFixed(2)}</td>
                    <td className="text-right"><button onClick={()=>remove(e.id)} className="p-1 text-[#5B6478] hover:text-rose-500"><Trash2 size={12}/></button></td>
                  </tr>
                ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="curio-card p-6">
          <h2 className="font-semibold mb-3">Add entry</h2>
          <form onSubmit={add} className="space-y-3" data-testid="add-entry-form">
            <div className="flex gap-2">
              <button type="button" onClick={()=>setForm({...form, kind: "expense"})} className={`text-xs px-3 py-1.5 rounded-full border flex-1 ${form.kind==='expense' ? 'bg-[#0B1220] text-white border-[#0B1220]' : 'border-[#E3E8F0]'}`}>Expense</button>
              <button type="button" onClick={()=>setForm({...form, kind: "income"})} className={`text-xs px-3 py-1.5 rounded-full border flex-1 ${form.kind==='income' ? 'bg-[#0B1220] text-white border-[#0B1220]' : 'border-[#E3E8F0]'}`}>Income</button>
            </div>
            <input value={form.tool} onChange={e=>setForm({...form, tool: e.target.value})} placeholder="Tool / source (e.g., ChatGPT)" className="w-full bg-[#F7F9FC] border border-[#E3E8F0] rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#0B1220]" data-testid="entry-tool"/>
            <div className="flex gap-2">
              <div className="relative flex-1"><DollarSign size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#5B6478]"/><input value={form.amount} onChange={e=>setForm({...form, amount: e.target.value})} placeholder="0.00" type="number" step="0.01" className="w-full bg-[#F7F9FC] border border-[#E3E8F0] rounded-xl pl-7 pr-3 py-2 text-sm focus:outline-none focus:border-[#0B1220]" data-testid="entry-amount"/></div>
              <select value={form.category} onChange={e=>setForm({...form, category: e.target.value})} className="bg-[#F7F9FC] border border-[#E3E8F0] rounded-xl px-3 py-2 text-sm">
                {CAT.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <input value={form.note} onChange={e=>setForm({...form, note: e.target.value})} placeholder="Note (optional)" className="w-full bg-[#F7F9FC] border border-[#E3E8F0] rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#0B1220]"/>
            <button className="btn-coral w-full justify-center" data-testid="add-entry-btn"><Plus size={13}/> Add</button>
          </form>
        </div>
      </div>

      {summary && summary.top_tools.length > 0 && (
        <div className="curio-card p-6 mb-6">
          <h2 className="font-semibold mb-3">Top spend by tool</h2>
          <div className="space-y-2">
            {summary.top_tools.map(t => {
              const pct = (t.amount / summary.total_expense) * 100;
              return (
                <div key={t.tool}>
                  <div className="flex justify-between text-sm mb-1"><span>{t.tool}</span><span className="font-semibold">${t.amount.toFixed(2)} · {pct.toFixed(0)}%</span></div>
                  <div className="h-1.5 bg-[#F1F3F8] rounded-full overflow-hidden"><div className="h-full curio-gradient" style={{ width: `${pct}%` }}/></div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {analysis && (
        <div className="curio-gradient text-white rounded-2xl p-8 fade-in" data-testid="analysis-panel">
          <div className="text-xs uppercase tracking-widest text-[#F97316] font-bold mb-2 flex items-center gap-2"><Sparkles size={13}/> AI Accountant · Report</div>
          <p className="whitespace-pre-wrap leading-relaxed">{analysis}</p>
        </div>
      )}
    </Layout>
  );
}
